import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subscription, withLatestFrom } from 'rxjs';
import { catchError, map, share, shareReplay, switchMap, take } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonTheme,
  ButtonType,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { ConfigService, LocationRouteCffService } from '@fcom/core/services';
import { PARTNER_AIRLINES, unsubscribe } from '@fcom/core/utils';
import { CmsArticle, CmsEditorialArticle, Location } from '@fcom/core-api';
import { FragmentService, LoaderType } from '@fcom/common';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core/interfaces';

import { Status, TransactionService } from '../../services/transaction.service';
import { Transaction, TransactionProductType, TransactionStatus } from '../../interfaces';

const TRANSACTION_ROWS_VISIBLE = 6; // transactions to appear in the table at a time

interface ModalTransaction extends Transaction {
  origin: string;
  destination: string;
  partner: { name: string; code: string };
}
enum NotificationContextType {
  Avios = 'avios',
  BetweenAvios = 'betweenAvios',
  TierPointTransaction = 'tierPointsTransaction',
}

@Component({
  selector: 'fin-transaction-history',
  templateUrl: 'transaction-history.component.html',
  styleUrls: ['./transaction-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TransactionHistoryComponent implements OnInit, OnDestroy {
  public readonly Status = Status;
  public readonly LoaderType = LoaderType;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonType = ButtonType;
  public readonly NotificationTheme = NotificationTheme;
  public readonly NotificationLayout = NotificationLayout;
  public readonly LoaderTheme = LoaderTheme;
  public readonly IconPosition = IconPosition;
  public readonly NotificationContextType = NotificationContextType;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  DISPLAYED_TRANSACTIONS = [
    TransactionStatus.PROCESSED,
    TransactionStatus.CANCELLED,
    TransactionStatus.PARTNER_PENDING,
    TransactionStatus.PARTNER_REJECT,
  ];

  @ViewChild('loadButton', { static: false })
  loadButton: ElementRef<HTMLButtonElement>;

  @ViewChild('openInfoModalButton', { static: false })
  openInfoModalButton: ElementRef;

  @Input()
  initialVisibleTransactionAmount: number = TRANSACTION_ROWS_VISIBLE;

  @Input()
  hideTooltip: boolean;

  @Input()
  showFullHistoryLink: boolean;

  @Input()
  hiddenCaptionText: string;

  ModalButtons = ModalButtons;
  TransactionStatus = TransactionStatus;
  TransactionProductType = TransactionProductType;
  LoyaltyRoutesPath = LoyaltyRoutesPath;
  visibleTransactions$: BehaviorSubject<Transaction[]> = new BehaviorSubject<Transaction[]>([]);
  modalTransaction$: ReplaySubject<ModalTransaction> = new ReplaySubject<ModalTransaction>(1);
  templateTransaction$: Observable<ModalTransaction>;
  plusChanges$: Observable<string>;
  visibleRowsAmount: number;
  allLoaded = false;
  loadingDisabled = false;
  status$: Observable<Status>;
  a11yStatusAddedRowsAmount: number;
  currentLanguage$: Observable<string>;
  isInfoModalOpen = false;
  isTransactionModalOpen = false;
  airlines = PARTNER_AIRLINES;
  aviosCutOfDate = this.configService.cfg.enableAviosOverhaul.isAviosCuttoffDateEffective;
  promoCta$: Observable<CmsEditorialArticle>;
  promoData$: Observable<CmsArticle>;
  transactionAvios$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  @ViewChildren('transactionRow', { read: ElementRef }) transactionRows: QueryList<ElementRef>;

  subscriptions: Subscription = new Subscription();

  constructor(
    private transactionService: TransactionService,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private configService: ConfigService,
    private fragmentService: FragmentService
  ) {}

  ngOnInit(): void {
    this.visibleRowsAmount = this.initialVisibleTransactionAmount;
    this.currentLanguage$ = this.languageService.lang;
    this.status$ = this.transactionService.status$;
    this.promoData$ = this.fragmentService.getFragment<CmsArticle>(
      'LOYALTY.fragments.transactionNotificationAvios.url'
    );
    this.promoCta$ = this.promoData$.pipe(map((data) => data as CmsEditorialArticle));
    this.subscriptions.add(
      this.status$.pipe(take(1)).subscribe((status) => {
        if (status === Status.INITIAL) {
          this.getTransactionFragment();
        } else {
          this.updateVisibleTransactions([]);
        }
      })
    );
    this.formatModalTransaction();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onLoadMoreTransactions(visibleTransactions: Transaction[]): void {
    const storeHasEnoughRows =
      this.transactionService.transactionsStore.length - visibleTransactions.length > this.visibleRowsAmount;
    const loadMoreToStore = !storeHasEnoughRows;
    if (storeHasEnoughRows) {
      const newRows = this.transactionService.transactionsStore.slice(
        visibleTransactions.length,
        visibleTransactions.length + this.visibleRowsAmount
      );
      this.visibleTransactions$.next(this.mapAnimationOrderToNewRows(visibleTransactions, newRows));
      this.a11yStatusAddedRowsAmount = newRows.length;
    } else if (loadMoreToStore && !this.allLoaded) {
      this.getTransactionFragment();
    } else {
      this.visibleTransactions$.next(
        this.mapAnimationOrderToNewRows(
          visibleTransactions,
          this.transactionService.transactionsStore.slice(visibleTransactions.length)
        )
      );
      this.loadingDisabled = true;
    }
  }

  onCloseNotification(): void {
    this.transactionService.status$.next(Status.INITIAL);
  }

  openTransactionModal(event: Event, transaction: Transaction): void {
    event.stopPropagation();
    this.modalTransaction$.next(transaction as ModalTransaction);
    this.isTransactionModalOpen = true;
  }

  closeTransactionModal(transactionId: string): void {
    this.transactionRows
      .find((item) => item.nativeElement.id === `transaction-row-${transactionId}`)
      ?.nativeElement.focus();
  }

  openInfoModal(event: MouseEvent): void {
    event.stopPropagation();
    this.isInfoModalOpen = true;
  }

  closeInfoModal(): void {
    if (this.openInfoModalButton) {
      this.openInfoModalButton.nativeElement.focus();
    }
  }

  private formatDescription(transaction): Observable<string> {
    const descriptionKey =
      transaction.status === TransactionStatus.CANCELLED
        ? 'plus.transactionHistory.cancelled'
        : 'plus.transactionHistory.rejected';
    return this.languageService.translate(descriptionKey).pipe(
      map((translation) => {
        return `${translation}: ${transaction.description}`;
      })
    );
  }

  private formatModalTransaction(): void {
    this.templateTransaction$ = this.modalTransaction$.pipe(
      switchMap((transaction) => {
        const newTransaction = { ...transaction, partner: { name: transaction.partnerName, code: '' } };
        const modalPartner = this.airlines.find((airline) => airline.code === newTransaction.partnerName);
        if (modalPartner) {
          newTransaction.partner = modalPartner;
        }
        if (newTransaction.originationAirport && newTransaction.destinationAirport && !newTransaction.origin) {
          return combineLatest([
            this.locationCodeToLocation(newTransaction.originationAirport),
            this.locationCodeToLocation(newTransaction.destinationAirport),
          ]).pipe(
            take(1),
            map(([origin, destination]) => {
              newTransaction.origin = `${origin.locationCode} ${origin.title}`;
              newTransaction.destination = `${destination.locationCode} ${destination.title}`;
              return newTransaction;
            }),
            catchError(() => {
              newTransaction.origin = newTransaction.originationAirport;
              newTransaction.destination = newTransaction.destinationAirport;
              return of(newTransaction);
            })
          );
        }
        return of(newTransaction);
      }),
      // eslint-disable-next-line rxjs/no-sharereplay
      shareReplay(1)
    );
  }

  private getTransactionFragment(): void {
    const getTransactionsRequest$ = this.transactionService.getTransactions().pipe(take(1), share());
    const getTransactionsNewTransactions$ = getTransactionsRequest$.pipe(
      map((transactions: Transaction[]) =>
        transactions.filter(({ status }) => this.DISPLAYED_TRANSACTIONS.includes(status))
      ),
      switchMap((transactions: Transaction[]) =>
        combineLatest(
          transactions.map((transaction) => {
            const mappedTransaction: Transaction = {
              ...transaction,
              awardPoints: this.formatPoints(transaction.awardPoints),
              tierPoints: this.formatPoints(transaction.tierPoints),
              description: transaction.description?.replace('PlusShop', 'Finnair Shop'),
            };
            if (
              (transaction.productType === TransactionProductType.AIRLINE_FLIGHT ||
                transaction.productType === TransactionProductType.AIRLINE_AWARD) &&
              transaction.originationAirport &&
              transaction.destinationAirport
            ) {
              return combineLatest([
                this.locationCodeToLocation(transaction.originationAirport),
                this.locationCodeToLocation(transaction.destinationAirport),
              ]).pipe(
                map(([orig, dest]) => ({
                  ...mappedTransaction,
                  description: this.createTransactionDescription(transaction, orig, dest),
                })),
                catchError(() => {
                  return of(mappedTransaction);
                })
              );
            }
            return of(mappedTransaction);
          })
        )
      ),
      switchMap((transactions: Transaction[]) =>
        combineLatest(
          transactions.map((transaction) => {
            if ([TransactionStatus.CANCELLED, TransactionStatus.PARTNER_REJECT].includes(transaction.status)) {
              return this.formatDescription(transaction).pipe(
                map((formatted) => ({
                  ...transaction,
                  description: formatted,
                }))
              );
            }
            return of(transaction);
          })
        )
      )
    );

    this.subscriptions.add(
      combineLatest([getTransactionsNewTransactions$, this.visibleTransactions$])
        .pipe(take(1), withLatestFrom(this.transactionService.status$))
        .subscribe(([[newTransactions, visibleTransactions], status]) => {
          if (status === Status.ALL_LOADED) {
            this.allLoaded = true;
          }
          if (newTransactions.length > 0) {
            this.transactionService.transactionsStore.push(...newTransactions);
            this.updateVisibleTransactions(visibleTransactions);
          }
        })
    );
  }

  private updateVisibleTransactions(visibleTransactions) {
    const newRows = this.transactionService.transactionsStore.slice(
      visibleTransactions.length,
      visibleTransactions.length + this.visibleRowsAmount
    );
    const newVisibleTransactions = this.mapAnimationOrderToNewRows(visibleTransactions, newRows);
    this.visibleTransactions$.next(newVisibleTransactions);
    this.a11yStatusAddedRowsAmount = newRows.length;
    if (this.allLoaded && newVisibleTransactions.length === this.transactionService.transactionsStore.length) {
      // Disable load more button if everything loaded and visible
      this.loadingDisabled = true;
    }
  }

  private createTransactionDescription(transaction: Transaction, orig: Location, dest: Location): string {
    if (transaction.productType === TransactionProductType.AIRLINE_AWARD) {
      return transaction.productName;
    }
    return `${orig.cityName} - ${dest.cityName}`;
  }

  private locationCodeToLocation(locationCode: string): Observable<Location> {
    return this.locationRouteCffService.bestGuessFor(locationCode, this.languageService.localeValue);
  }

  private formatPoints(value: string | undefined): string {
    if (value === undefined) {
      return '0';
    } else if (value.charAt(0) === '+') {
      return value;
    } else if (value.charAt(0) !== '-' && parseInt(value, 10) > 0) {
      value = `+${value}`;
    }
    return value;
  }

  private mapAnimationOrderToNewRows(currentRows: Transaction[], newRows: Transaction[]): Transaction[] {
    return [
      ...currentRows.map((row) => {
        return { ...row, animationOrder: undefined };
      }),
      ...newRows.map((row, index) => {
        return { ...row, animationOrder: index + 1 };
      }),
    ];
  }

  getNotificationContext(transaction: ModalTransaction): { context: NotificationContextType } {
    const ayBookingDate = transaction.ayBookingDate ? new Date(transaction.ayBookingDate) : undefined;
    const transactionDate = new Date(transaction.date);
    const cutOffDate = new Date(this.aviosCutOfDate);
    const transactionProductType = transaction.productType;
    if (!ayBookingDate && transactionDate > cutOffDate) {
      this.transactionAvios$.next(NotificationContextType.Avios);
      return { context: NotificationContextType.Avios };
    }
    if (!ayBookingDate && transactionDate < cutOffDate) {
      this.transactionAvios$.next(NotificationContextType.TierPointTransaction);
      return { context: NotificationContextType.TierPointTransaction };
    }
    const contextType =
      ayBookingDate > cutOffDate &&
      transactionDate > cutOffDate &&
      transactionProductType === TransactionProductType.AIRLINE_FLIGHT
        ? NotificationContextType.Avios
        : ayBookingDate < cutOffDate &&
            transactionDate > cutOffDate &&
            transactionProductType === TransactionProductType.AIRLINE_FLIGHT
          ? NotificationContextType.BetweenAvios
          : NotificationContextType.TierPointTransaction;
    this.transactionAvios$.next(contextType);
    return { context: contextType };
  }
}
